<template>
  <div class="container-fluid">
    <div class="card mb-25 border-0 rounded-0 bg-white shadow-sm">
      <!-- Header bleu -->
      <div class="card-header d-flex justify-content-between align-items-center py-4 px-4"
        style="background-color: #0a59a4;">
        <div>
          <h3 class="text-white fw-bold mb-1">
            Détail de la règle de tarification – {{ rule.code || 'Chargement...' }}
          </h3>
          <small class="text-white opacity-75">
            Nom : {{ rule.name }} • Créée le {{ formatDate(rule.createdAt) }}
          </small>
        </div>
        <div>
          <span class="badge fs-5 px-4 py-2" :class="rule.isActive ? 'bg-success' : 'bg-secondary'">
            {{ rule.isActive ? 'Active' : 'Inactive' }}
          </span>
        </div>
      </div>

      <div class="card-body p-15 p-sm-20 p-md-30 letter-spacing">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Chargement...</span>
          </div>
        </div>

        <template v-else>
          <!-- Alerte règle d'exemption -->
          <div v-if="rule.isExemptionRule" class="alert alert-warning border-0 shadow-sm mb-4">
            <div class="d-flex align-items-center gap-3">
              <i class="fas fa-percentage fa-2x text-warning"></i>
              <div>
                <h5 class="mb-1 fw-bold">
                  RÈGLE D'EXEMPTION (RÉDUCTION)
                  <span class="badge bg-warning text-dark ms-2">
                    {{ rule.exemptionRate ? rule.exemptionRate + '%' : 'Non défini' }}
                  </span>
                </h5>
                <p class="mb-0">Cette règle applique une réduction sur les montants calculés.</p>
              </div>
            </div>
          </div>

          <!-- ===== ÉTAPE 1 : INFORMATIONS DE BASE ===== -->
          <div class="alert alert-primary mb-4">
            <strong>Étape 1 :</strong> Informations de base de la règle
          </div>

          <div class="row g-4 mb-5">
            <div class="col-md-4">
              <label class="d-block text-black fw-semibold mb-1">Code</label>
              <p class="mb-0 fw-bold">{{ rule.code || '-' }}</p>
            </div>
            <div class="col-md-4">
              <label class="d-block text-black fw-semibold mb-1">Nom</label>
              <p class="mb-0">{{ rule.name || '-' }}</p>
            </div>
            <div class="col-md-4">
              <label class="d-block text-black fw-semibold mb-1">Zone Tarifaire</label>
              <p class="mb-0">{{ rule.tariffZone?.code ? `${rule.tariffZone.code} - ${rule.tariffZone.name}` : '-' }}</p>
            </div>

            <div class="col-md-4">
              <label class="d-block text-black fw-semibold mb-1">Type d'entité</label>
              <p class="mb-0">
                <span class="badge bg-primary">{{ getEntityTypeLabel(rule.appliesToEntityType) }}</span>
              </p>
            </div>

            <div class="col-md-4">
              <label class="d-block text-black fw-semibold mb-1">Priorité</label>
              <p class="mb-0">
                <strong>{{ rule.priority }}</strong>
                <small class="text-muted ms-2">(plus le nombre est bas, plus prioritaire)</small>
              </p>
            </div>

            <div class="col-md-4">
              <label class="d-block text-black fw-semibold mb-1">Statut</label>
              <p class="mb-0">
                <span class="badge" :class="rule.isActive ? 'bg-success' : 'bg-secondary'">
                  {{ rule.isActive ? 'Active' : 'Inactive' }}
                </span>
              </p>
            </div>

            <div class="col-md-6">
              <label class="d-block text-black fw-semibold mb-1">Date de début</label>
              <p class="mb-0">{{ rule.effectiveFrom ? formatDate(rule.effectiveFrom) : 'Immédiate' }}</p>
            </div>
            <div class="col-md-6">
              <label class="d-block text-black fw-semibold mb-1">Date de fin</label>
              <p class="mb-0">{{ rule.effectiveTo ? formatDate(rule.effectiveTo) : 'Sans limite' }}</p>
            </div>
          </div>

          <!-- ===== ÉTAPE 2 & 3 : CONFIGURATION PRINCIPALE ===== -->
          <div class="alert alert-warning mb-4">
            <strong>Étape 3 :</strong> Configuration principale de la règle ({{ getEntityTypeLabel(rule.appliesToEntityType) }})
          </div>

          <div class="row g-4 mb-5">
            <!-- BESC / BL -->
            <template v-if="['BESC', 'BL'].includes(rule.appliesToEntityType)">
              <div class="col-md-6">
                <label class="d-block text-black fw-semibold mb-1">Montant de base</label>
                <p class="mb-0 fw-bold fs-5">
                  {{ formatNumber(rule.baseAmount) }} {{ rule.amountCurrency || 'EUROS' }}
                </p>
              </div>
              <div class="col-md-6">
                <label class="d-block text-black fw-semibold mb-1">Devise</label>
                <p class="mb-0">{{ rule.amountCurrency || '-' }}</p>
              </div>
            </template>

            <!-- CONTAINER -->
            <template v-if="rule.appliesToEntityType === 'CONTAINER'">
              <div class="col-md-6">
                <label class="d-block text-black fw-semibold mb-1">Montant par conteneur</label>
                <p class="mb-0 fw-bold fs-5">
                  {{ formatNumber(rule.perContainerAmount) }} {{ rule.amountCurrency || 'EUROS' }}
                </p>
              </div>
              <div class="col-md-6">
                <label class="d-block text-black fw-semibold mb-1">Devise</label>
                <p class="mb-0">{{ rule.amountCurrency || '-' }}</p>
              </div>

              <div class="col-md-12" v-if="metadata.containerTypes?.length">
                <label class="d-block text-black fw-semibold mb-1">Types de conteneur concernés</label>
                <div class="d-flex flex-wrap gap-2 mt-2">
                  <span v-for="type in metadata.containerTypes" :key="type.id || type" class="badge bg-info fs-6">
                    {{ type.name || type.label || type.libelle || type }}
                  </span>
                </div>
              </div>

              <div class="col-md-12" v-if="metadata.requireDistantOrigin">
                <div class="alert alert-info mt-3">
                  <i class="fas fa-globe me-2"></i>
                  <strong>Uniquement pour origines lointaines</strong> (Asie, Amérique, Océanie)
                </div>
              </div>
            </template>

            <!-- CARGO_LINE -->
            <template v-if="rule.appliesToEntityType === 'CARGO_LINE'">
              <div class="col-md-4">
                <label class="d-block text-black fw-semibold mb-1">Catégorie de produit</label>
                <p class="mb-0">{{ rule.categorieProduit?.name || '-' }}</p>
              </div>
              <div class="col-md-4">
                <label class="d-block text-black fw-semibold mb-1">Montant par ligne</label>
                <p class="mb-0 fw-bold fs-5">
                  {{ formatNumber(rule.baseAmount) }} {{ rule.amountCurrency || 'EUROS' }}
                </p>
              </div>
              <div class="col-md-4">
                <label class="d-block text-black fw-semibold mb-1">Devise</label>
                <p class="mb-0">{{ rule.amountCurrency || '-' }}</p>
              </div>
            </template>

            <!-- GOODS -->
            <template v-if="rule.appliesToEntityType === 'GOODS'">
              <!-- SI AVEC CONDITIONNEMENT -->
              <template v-if="rule.packagingType">
                <div class="col-md-12 mb-3">
                  <div class="alert alert-info">
                    <i class="fas fa-box me-2"></i>
                    <strong>Marchandises avec conditionnement</strong>
                  </div>
                </div>

                <div class="col-md-4">
                  <label class="d-block text-black fw-semibold mb-1">Type de conditionnement</label>
                  <p class="mb-0">
                    <span class="badge bg-primary">{{ getPackagingTypeLabel(rule.packagingType) }}</span>
                  </p>
                </div>

                <div class="col-md-4">
                  <label class="d-block text-black fw-semibold mb-1">Taille de la tranche</label>
                  <p class="mb-0">{{ rule.trancheSize || '-' }}</p>
                </div>

                <div class="col-md-4">
                  <label class="d-block text-black fw-semibold mb-1">Unité de mesure</label>
                  <p class="mb-0">{{ getUnitOfMeasureLabel(rule.unitOfMeasure) || '-' }}</p>
                </div>

                <!-- Pour ROULANT uniquement -->
                <template v-if="['ROULANT_LEGER', 'ROULANT_LOURD'].includes(rule.packagingType)">
                  <div class="col-md-4">
                    <label class="d-block text-black fw-semibold mb-1">Poids minimum (tonnes)</label>
                    <p class="mb-0">{{ rule.minWeight || '0' }}</p>
                  </div>
                  <div class="col-md-4">
                    <label class="d-block text-black fw-semibold mb-1">Poids maximum (tonnes)</label>
                    <p class="mb-0">{{ rule.maxWeight || 'Illimité' }}</p>
                  </div>
                </template>

                <div class="col-md-4">
                  <label class="d-block text-black fw-semibold mb-1">Max par ECTN</label>
                  <p class="mb-0">{{ rule.maxPerEctn || '-' }}</p>
                </div>

                <!-- TARIFS PAR ORIGINE -->
                <div class="col-md-12 mt-3">
                  <div class="alert alert-success">
                    <strong><i class="fas fa-globe me-2"></i>Tarifs par origine géographique</strong>
                  </div>
                </div>

                <div class="col-md-6">
                  <label class="d-block text-black fw-semibold mb-1">
                    <i class="fas fa-map-marker-alt text-primary me-2"></i>
                    Tarif Europe/Afrique (origines locales)
                  </label>
                  <p class="mb-0 fw-bold fs-5">
                    {{ metadata.tariffs?.localOrigins ? formatNumber(metadata.tariffs.localOrigins) + ' ' + (rule.amountCurrency || 'EUR') : '-' }}
                  </p>
                </div>

                <div class="col-md-6">
                  <label class="d-block text-black fw-semibold mb-1">
                    <i class="fas fa-globe-americas text-warning me-2"></i>
                    Tarif Asie/Amérique/Océanie (origines lointaines)
                  </label>
                  <p class="mb-0 fw-bold fs-5">
                    {{ metadata.tariffs?.distantOrigins ? formatNumber(metadata.tariffs.distantOrigins) + ' ' + (rule.amountCurrency || 'EUR') : '-' }}
                  </p>
                </div>

                <div class="col-md-4">
                  <label class="d-block text-black fw-semibold mb-1">Devise</label>
                  <p class="mb-0">{{ rule.amountCurrency || '-' }}</p>
                </div>
              </template>

              <!-- SI SANS CONDITIONNEMENT (ancien système) -->
              <template v-else>
                <div class="col-md-4">
                  <label class="d-block text-black fw-semibold mb-1">Catégorie de produit</label>
                  <p class="mb-0">{{ rule.categorieProduit?.name || '-' }}</p>
                </div>
                <div class="col-md-4">
                  <label class="d-block text-black fw-semibold mb-1">Montant fixe par marchandise</label>
                  <p class="mb-0">{{ rule.baseAmount ? formatNumber(rule.baseAmount) + ' ' + (rule.amountCurrency || 'EUROS') : '-' }}</p>
                </div>
                <div class="col-md-4">
                  <label class="d-block text-black fw-semibold mb-1">OU Taux % sur la valeur</label>
                  <p class="mb-0">{{ rule.percentageRate ? rule.percentageRate + '%' : '-' }}</p>
                </div>
                <div class="col-md-4">
                  <label class="d-block text-black fw-semibold mb-1">Devise</label>
                  <p class="mb-0">{{ rule.amountCurrency || '-' }}</p>
                </div>
              </template>
            </template>

            <!-- VALUE_BRACKET -->
            <template v-if="rule.appliesToEntityType === 'VALUE_BRACKET'">
              <div class="col-md-4">
                <label class="d-block text-black fw-semibold mb-1">Valeur minimum (EUROS)</label>
                <p class="mb-0">{{ formatNumber(rule.minDeclaredValue) || '0' }}</p>
              </div>
              <div class="col-md-4">
                <label class="d-block text-black fw-semibold mb-1">Valeur maximum (EUROS)</label>
                <p class="mb-0">{{ rule.maxDeclaredValue ? formatNumber(rule.maxDeclaredValue) : 'Illimité' }}</p>
              </div>
              <div class="col-md-4">
                <label class="d-block text-black fw-semibold mb-1">Taux appliqué (%)</label>
                <p class="mb-0">{{ rule.percentageRate ? rule.percentageRate + '%' : '-' }}</p>
              </div>
              <div class="col-md-12">
                <label class="d-block text-black fw-semibold mb-1">OU Montant fixe (EUROS)</label>
                <p class="mb-0">{{ rule.baseAmount ? formatNumber(rule.baseAmount) : '-' }}</p>
              </div>
            </template>
          </div>

                    <!-- ===== ÉTAPE 4 : OPTIONS AVANCÉES ===== -->
          <div class="alert alert-secondary mb-4">
            <strong>Étape 4 :</strong> Options avancées (facultatif)
          </div>

          <div class="d-flex flex-wrap align-items-center gap-4 mb-5 border p-4 rounded bg-light">
            <!-- Continents du port de chargement -->
            <div v-if="metadata.loadingPortRegion?.length" class="d-flex align-items-center gap-2">
              <label class="text-black fw-semibold mb-0 text-nowrap">
                <i class="fas fa-globe me-2"></i>
                Continents du port de chargement :
              </label>
              <div class="d-flex flex-wrap gap-2">
                <span v-for="region in metadata.loadingPortRegion" :key="region" class="badge bg-secondary">
                  {{ getContinentLabel(region) }}
                </span>
              </div>
            </div>

            <!-- Régime douanier -->
            <div v-if="rule.regimedouanier" class="d-flex align-items-center gap-2">
              <label class="text-black fw-semibold mb-0 text-nowrap">Régime douanier :</label>
              <span class="fw-bold">{{ rule.regimedouanier.name || '-' }}</span>
            </div>

            <!-- Montant minimum facturé -->
            <div class="d-flex align-items-center gap-2">
              <label class="text-black fw-semibold mb-0 text-nowrap">Montant minimum facturé :</label>
              <span class="fw-bold text-success">
                {{ rule.minFeeAmount ? formatNumber(rule.minFeeAmount) + ' EUROS' : 'Aucun' }}
              </span>
            </div>

            <!-- Montant maximum facturé -->
            <div class="d-flex align-items-center gap-2">
              <label class="text-black fw-semibold mb-0 text-nowrap">Montant maximum facturé :</label>
              <span class="fw-bold text-danger">
                {{ rule.maxFeeAmount ? formatNumber(rule.maxFeeAmount) + ' EUROS' : 'Aucun' }}
              </span>
            </div>

            <!-- Message si aucune option avancée -->
            <div v-if="!metadata.loadingPortRegion?.length && !rule.regimedouanier && !rule.minFeeAmount && !rule.maxFeeAmount"
                 class="text-muted fst-italic">
              Aucune option avancée définie
            </div>
          </div>

          <!-- Description -->
          <div class="mb-4" v-if="rule.description">
            <h5 class="text-primary fw-bold mb-3">Description</h5>
            <div class="bg-light p-4 rounded">
              <p class="mb-0">{{ rule.description }}</p>
            </div>
          </div>

          <!-- Boutons -->
          <div class="d-flex justify-content-between mt-5 pt-4 border-top">
            <router-link to="/listeBescPricingRule" class="btn btn-secondary px-5 py-2">
              Retour à la liste
            </router-link>
            <!-- <router-link :to="{ name: 'EditBescPricingRulePage', params: { id: rule.id } }"
              class="btn btn-warning px-5 py-2 text-white">
              Modifier la règle
            </router-link> -->
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<!-- Le script reste EXACTEMENT le même -->
<script lang="ts">
import { defineComponent, ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ApiService from '@/services/ApiService';
import { error } from '@/utils/utils';

export default defineComponent({
  name: 'ViewPricingRule',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const rule = ref<any>({});
    const metadata = ref<any>({});
    const loading = ref(true);

    const entityTypeLabels: Record<string, string> = {
      BESC: 'BESC - Dossier complet',
      BL: 'BL - Connaissement',
      CONTAINER: 'CONTAINER - Conteneur',
      CARGO_LINE: 'CARGO_LINE - Ligne de marchandise',
      GOODS: 'GOODS - Marchandises (par catégorie)',
      VALUE_BRACKET: 'VALUE_BRACKET - Tranche de valeur'
    };

    const getPackagingTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      FCL: 'FCL - Conteneurs pleins',
      ROULANT_LEGER: 'Roulant Léger (< 5 tonnes)',
      ROULANT_LOURD: 'Roulant Lourd (≥ 5 tonnes)',
      GROUPAGE: 'Groupage',
      CONVENTIONNEL: 'Conventionnel/Vrac',
      VRAC_COMPLET: 'Vrac navire complet'
    };
    return labels[type] || type;
  };

  const getUnitOfMeasureLabel = (unit: string) => {
    const labels: Record<string, string> = {
      CONTENEUR: 'Conteneur(s)',
      VEHICULE: 'Véhicule(s)',
      TONNE_METRIQUE: 'Tonne(s) métrique(s)'
    };
    return labels[unit] || unit;
  };

    const getEntityTypeLabel = (type: string) => {
      return entityTypeLabels[type] || type;
    };

    const formatDate = (date: string) => {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('fr-FR');
    };

    const formatNumber = (val: any) => {
      if (val === null || val === undefined) return '-';
      return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(val);
    };

    const fetchRule = async () => {
      try {
        const id = route.params.id as string;
        const response = await ApiService.get(`/pricing-rule/${id}`);
        rule.value = response.data.data;

        if (rule.value.metadata) {
          try {
            metadata.value = JSON.parse(rule.value.metadata);
          } catch (e) {
            metadata.value = {};
          }
        } else {
          metadata.value = {};
        }

        loading.value = false;
      } catch (err: any) {
        error(err.response?.data?.message || 'Impossible de charger la règle');
        router.push('/listeBescPricingRule');
      }
    };
    const getContinentLabel = (value: string) => {
  const map: Record<string, string> = {
    AFRIQUE: 'Afrique',
    EUROPE: 'Europe',
    ASIE: 'Asie',
    AMERIQUE: 'Amérique',
    OCEANIE: 'Océanie',
  };
  return map[value] || value;
};

    onMounted(() => {
      fetchRule();
    });

    return {
      rule,
      metadata,
      loading,
      getPackagingTypeLabel,
      getUnitOfMeasureLabel,
      getContinentLabel,
      getEntityTypeLabel,
      formatDate,
      formatNumber
    };
  }
});
</script>

<style scoped>
.text-primary { color: #0a59a4 !important; }
.badge { font-size: 0.9em; }
.fs-5 { font-size: 1.1rem !important; }
</style>